import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getSeasonConfig } from "@/lib/seasonalConfig";
import { supabase } from "@/integrations/supabase/client";

interface Deal { text: string; badge: string; link: string }

const AnnouncementBanner = () => {
  const { announcementDeals: seasonalDeals } = getSeasonConfig();
  const [deals, setDeals] = useState<Deal[]>(seasonalDeals);

  useEffect(() => {
    let mounted = true;
    let timerId: ReturnType<typeof globalThis.setTimeout> | undefined;
    let idleId: number | undefined;
    const loadCampaign = async () => {
      try {
        const { data } = await supabase
          .from("active_campaigns")
          .select("name, slug, discount_percent, status")
          .eq("status", "running")
          .order("started_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!mounted || !data) return;
        const campaignDeal: Deal = {
          text: `🔥 ${data.name} – spara ${Number(data.discount_percent)}%!`,
          badge: `-${Number(data.discount_percent)}%`,
          link: `/kampanj/${data.slug}`,
        };
        setDeals([campaignDeal, ...seasonalDeals.slice(0, 2)]);
      } catch { /* keep seasonal */ }
    };
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(loadCampaign, { timeout: 2500 });
    } else {
      timerId = globalThis.setTimeout(loadCampaign, 1500);
    }
    return () => {
      mounted = false;
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timerId !== undefined) globalThis.clearTimeout(timerId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-gradient-to-r from-primary-800 via-primary-700 to-primary-800 overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.06)_0%,transparent_50%),radial-gradient(circle_at_80%_50%,rgba(255,255,255,0.04)_0%,transparent_50%)]" />
      
      <div className="relative container px-4">
        <div className="hidden md:flex items-center justify-center gap-0 py-2">
          {deals.map((deal, i) => (
            <Link
              key={i}
              to={deal.link}
              className="flex items-center gap-3 px-6 group hover:bg-white/5 rounded-lg py-1 transition-colors"
            >
              <span className="inline-flex items-center justify-center bg-amber-400 text-primary-900 text-xs font-black rounded-full h-8 min-w-8 px-2 shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                {deal.badge}
              </span>
              <span className="text-white/95 text-sm font-semibold tracking-wide whitespace-nowrap">
                {deal.text}
              </span>
              {i < deals.length - 1 && (
                <span aria-hidden="true" className="text-white/15 ml-3 text-lg">|</span>
              )}
            </Link>
          ))}
        </div>

        <div className="md:hidden py-2.5 px-3 overflow-hidden">
          <div className="animate-marquee flex items-center gap-8 whitespace-nowrap">
            {[...Array(3)].map((_, repeat) => (
              <div key={repeat} className="flex items-center gap-6 shrink-0">
                {deals.map((deal, i) => (
                  <Link
                    key={i}
                    to={deal.link}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <span className="inline-flex items-center justify-center bg-amber-400 text-primary-900 text-[10px] font-black rounded-full h-6 min-w-6 px-1.5 shrink-0">
                      {deal.badge}
                    </span>
                    <span className="text-white/95 text-xs font-semibold tracking-wide">
                      {deal.text}
                    </span>
                  </Link>
                ))}
                <span aria-hidden="true" className="text-white/15 shrink-0">·</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
